import {
  cloneElement,
  isValidElement,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactElement,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { DatePicker } from "./DatePicker";
import { cn } from "../lib/cn";

type FieldControlProps = {
  id?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
};

export function Field({
  label,
  hint,
  error,
  required,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
}) {
  // Colons from useId() break label association in Safari.
  const uid = useId().replace(/:/g, "");
  const child = isValidElement<FieldControlProps>(children) ? children : null;
  const id = child?.props.id ?? uid;
  const hintId = hint && !error ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(" ") || undefined;

  const control =
    child != null
      ? cloneElement(child as ReactElement<FieldControlProps>, {
          id,
          "aria-invalid": error ? true : undefined,
          "aria-describedby": describedBy,
        })
      : children;

  // Do not wrap the control in <label>: on macOS/Safari, Space activates the
  // label (select-all / replace), which looks like "I can't type" or "space
  // deleted everything". Interactive children (password toggle) make it worse.
  return (
    <div className="min-w-0 max-w-full">
      <label htmlFor={id} className="mb-1 block text-sm font-medium text-ink-soft">
        {label}
        {required ? <span className="text-stop"> *</span> : null}
      </label>
      {control}
      {hint && !error ? (
        <span id={hintId} className="mt-1 block text-xs text-ink-muted">
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={errorId} className="mt-1 block text-xs text-stop">
          {error}
        </span>
      ) : null}
    </div>
  );
}

const fieldCls =
  "z-field w-full min-w-0 max-w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-base text-ink placeholder:text-slate-400 focus:border-brand-500 focus:ring-0";

const TEMPORAL_TYPES = new Set(["date", "datetime-local", "time", "month", "week"]);

export function Input({ className, type, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  if (type && TEMPORAL_TYPES.has(type)) {
    return <DatePicker className={className} type={type} {...rest} />;
  }
  return <input className={cn(fieldCls, className)} type={type} {...rest} />;
}

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
        <path
          d="M3 3l18 18M10.6 10.6a2 2 0 0 0 2.8 2.8M9.9 5.1A9.8 9.8 0 0 1 12 5c5 0 9.3 3.1 11 7.5a11.6 11.6 0 0 1-4.1 5.1M6.1 6.1A11.6 11.6 0 0 0 1 12.5C2.7 16.9 7 20 12 20c1.7 0 3.3-.3 4.8-1"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden="true">
      <path
        d="M1 12.5C2.7 8.1 7 5 12 5s9.3 3.1 11 7.5C21.3 16.9 17 20 12 20S2.7 16.9 1 12.5Z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

export function PasswordInput({ className, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, "type">) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <input
        {...rest}
        type={visible ? "text" : "password"}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        className={cn(fieldCls, "pr-11", className)}
      />
      <button
        type="button"
        className="absolute inset-y-0 right-0 flex items-center px-3 text-ink-muted hover:text-ink"
        aria-label={visible ? "Ocultar contraseña" : "Mostrar contraseña"}
        aria-pressed={visible}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setVisible((v) => !v)}
      >
        <EyeIcon open={visible} />
      </button>
    </div>
  );
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return <textarea className={cn(fieldCls, "min-h-24", className)} {...rest} />;
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cn(fieldCls, "appearance-none", className)} {...rest}>
      {children}
    </select>
  );
}
